import {CommitAttribution} from '@github-ui/commits/components/CommitAttribution'
import {CopySHA} from '@github-ui/commits/components/CopySHA'
import {DeferredCommitHeaderData} from '@github-ui/commits/components/DeferredCommitHeaderData'
import {useLoadSingleDeferredCommitData} from '@github-ui/commits/shared/useLoadDeferredCommitData'
import type {CommitExtended} from '@github-ui/commits/types/commit-types'
import {shortSha} from '@github-ui/commits/utils/short-sha'
import {commitPath, repositoryTreePath, pullRequestFilesChangedCommitRangePath} from '@github-ui/paths'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {FileCodeIcon, ChevronLeftIcon, ChevronRightIcon} from '@primer/octicons-react'
import {Button, Link, IconButton} from '@primer/react'
import {useMemo} from 'react'
import {useNavigate} from '@github-ui/use-navigate'

import type {PullRequestAppPayload} from '../page-data/payloads/pull-request-app'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import type {CommitsSelectorCommitData} from '../page-data/payloads/file-tree'

type PullRequestCommitHeaderProps = {
  commit: CommitExtended
  pullRequest?: FilesRoutePayload['pullRequest']
  commits?: CommitsSelectorCommitData[]
  repository: FilesRoutePayload['repository']
}

export function PullRequestCommitHeader({commit, pullRequest, commits = [], repository}: PullRequestCommitHeaderProps) {
  const {helpUrl} = useAppPayload<PullRequestAppPayload>()
  const navigate = useNavigate()

  // Navigation logic for prev/next commit
  const {prevCommitUrl, nextCommitUrl, canNavigate} = useMemo(() => {
    if (commits.length === 0) {
      return {prevCommitUrl: null, nextCommitUrl: null, canNavigate: false}
    }
    const currentCommitOid = commit.oid
    const currentIndex = commits.findIndex(c => c.oid === currentCommitOid)
    if (currentIndex === -1) {
      return {prevCommitUrl: null, nextCommitUrl: null, canNavigate: false}
    }
    const prevCommit = currentIndex > 0 ? commits[currentIndex - 1] : null
    const nextCommit = currentIndex < commits.length - 1 ? commits[currentIndex + 1] : null
    const basePath = {
      owner: repository.ownerLogin,
      repo: repository.name,
      number: pullRequest?.number || 0,
    }
    const prevUrl = prevCommit ? pullRequestFilesChangedCommitRangePath({...basePath, head: prevCommit.oid}) : null
    const nextUrl = nextCommit ? pullRequestFilesChangedCommitRangePath({...basePath, head: nextCommit.oid}) : null
    return {
      prevCommitUrl: prevUrl,
      nextCommitUrl: nextUrl,
      canNavigate: true,
    }
  }, [commits, commit, pullRequest, repository])

  const commitOid = commit.oid
  const deferredData = useLoadSingleDeferredCommitData(
    `${commitPath({
      owner: repository.ownerLogin,
      repo: repository.name,
      commitish: commitOid,
    })}/deferred_commit_data`,
  )

  return (
    <div className="d-flex flex-column border rounded-2 overflow-hidden mb-3">
      <div className="d-flex flex-row flex-justify-between border-bottom p-3 bgColor-inset flex-items-center">
        <div className="flex-1">
          <VerifiedHTMLText html={commit.shortMessageMarkdown} className="ws-pre-wrap f5 wb-break-word" />
          {commit.bodyMessageHtml && (
            <VerifiedHTMLText html={commit.bodyMessageHtml} className="ws-pre-wrap f6 wb-break-word text-mono mt-2" />
          )}
        </div>
        <div className="d-flex flex-items-center gap-2 ml-2">
          {canNavigate && (
            <div className="d-flex">
              {prevCommitUrl ? (
                <IconButton
                  as="button"
                  onClick={() => navigate(prevCommitUrl)}
                  icon={ChevronLeftIcon}
                  aria-label="Previous commit"
                  size="small"
                  variant="invisible"
                />
              ) : (
                <IconButton
                  as="button"
                  icon={ChevronLeftIcon}
                  aria-label="Previous commit"
                  size="small"
                  disabled
                  variant="invisible"
                />
              )}
              {nextCommitUrl ? (
                <IconButton
                  as="button"
                  onClick={() => navigate(nextCommitUrl)}
                  icon={ChevronRightIcon}
                  aria-label="Next commit"
                  size="small"
                  variant="invisible"
                />
              ) : (
                <IconButton
                  as="button"
                  icon={ChevronRightIcon}
                  aria-label="Next commit"
                  size="small"
                  disabled
                  variant="invisible"
                />
              )}
            </div>
          )}
          <Button
            as={Link}
            href={repositoryTreePath({repo: repository, action: 'tree', commitish: commitOid})}
            leadingVisual={FileCodeIcon}
            size="small"
          >
            Browse files
          </Button>
        </div>
      </div>
      <div className="d-flex flex-row flex-justify-between p-3 f5">
        <div>
          <CommitAttribution
            commit={commit}
            repo={repository}
            settings={{
              fontColor: 'fg.default',
            }}
            textVariant="muted"
          >
            <DeferredCommitHeaderData
              deferredData={deferredData}
              oid={commit.oid}
              repo={repository}
              helpUrl={helpUrl}
            />
          </CommitAttribution>
        </div>
        <div className="d-flex flex-items-center gap-2">
          <span className="color-fg-muted">Commit</span>
          <Link
            href={commitPath({owner: repository.ownerLogin, repo: repository.name, commitish: commitOid})}
            className="Link--primary"
          >
            <code>{shortSha(commitOid)}</code>
          </Link>
          <CopySHA sha={commitOid} direction="sw" />
        </div>
      </div>
    </div>
  )
}

try{ PullRequestCommitHeader.displayName ||= 'PullRequestCommitHeader' } catch {}